import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { FiPrinter } from 'react-icons/fi';

const BillPrint = () => {
  const { checkinId } = useParams();
  const [billData, setBillData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBillData();
  }, [checkinId]);

  const fetchBillData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/vendor/checkin-management/bill/${checkinId}`);
      if (response.data.success) {
        setBillData(response.data.bill);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch bill data');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p>Loading...</p>
      </div>
    );
  }

  if (!billData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p>Bill not found</p>
      </div>
    );
  }

  const { vendor, guest, booking, persons, charges, payments } = billData;

  const nights = booking?.checkinDateTime && booking?.checkoutDateTime
    ? Math.ceil((new Date(booking.checkoutDateTime) - new Date(booking.checkinDateTime)) / (1000 * 60 * 60 * 24))
    : 0;

  const totalGuests = persons?.length || 0;

  const balance =
    (charges?.totalAmount || 0) -
    ((payments?.advancePaid || 0) + (payments?.settledAmount || 0) + (payments?.tipAmount || 0));

  return (
    <div className="bg-gray-100 p-2 print:p-0">

      {/* Print Button */}
      <div className="max-w-[300px] mx-auto mb-2 print:hidden">
        <button
          onClick={handlePrint}
          className="w-full py-2 bg-blue-600 text-white rounded flex items-center justify-center gap-2"
        >
          <FiPrinter /> Print
        </button>
      </div>

      {/* Bill */}
      <div className="max-w-[300px] mx-auto bg-white shadow print:shadow-none print:break-inside-avoid">

        <div className="p-3 text-[11px] leading-tight space-y-2">

          {/* Header */}
          <div className="text-center break-inside-avoid">
            <h1 className="font-bold text-sm">{vendor?.businessName}</h1>
            <p>{vendor?.address}</p>
            {vendor?.contactNumber && <p>{vendor.contactNumber}</p>}
            {vendor?.gstNumber && <p>GST: {vendor.gstNumber}</p>}
          </div>

          {/* Bill Info */}
          <div className="border-y border-dashed py-1 space-y-0.5 break-inside-avoid">
            <div className="flex justify-between"><span>Bill#</span><span>{billData.billNumber}</span></div>
            <div className="flex justify-between"><span>Room</span><span>{booking?.roomNumber}</span></div>
            <div className="flex justify-between"><span>Date</span><span>{new Date(billData.billDate).toLocaleDateString()}</span></div>
          </div>

          {/* Booking Details */}
          <div className="space-y-0.5 break-inside-avoid">
            <div className="flex justify-between"><span>Nights</span><span>{nights}</span></div>
            <div className="flex justify-between"><span>Guests</span><span>{totalGuests}</span></div>
          </div>

          {/* Guest Details Table */}
          <div className="border-t border-dashed pt-1 break-inside-avoid">
            <div className="grid grid-cols-2 gap-1 text-xs">
              <div className="font-semibold">Guest Name:</div>
              <div>{guest?.name || 'N/A'}</div>
              <div className="font-semibold">Mobile:</div>
              <div>{guest?.mobileNumber || 'N/A'}</div>
              <div className="font-semibold">Email:</div>
              <div className="font-semibold">ID Number:</div>
              <div>{guest?.identityNumber || 'N/A'}</div>
            </div>
          </div>

          {/* Charges */}
          <div className="border-t-2 pt-1 space-y-0.5 break-inside-avoid">
            <div className="flex justify-between">
              <span>Room</span><span>₹{charges?.baseAmount?.toFixed(2)}</span>
            </div>

            {charges?.otherAmount > 0 && (
              <div className="flex justify-between">
                <span>Other</span><span>₹{charges.otherAmount.toFixed(2)}</span>
              </div>
            )}

            {payments?.discountAmount > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Discount</span><span>-₹{payments.discountAmount.toFixed(2)}</span>
              </div>
            )}

            <div className="flex justify-between border-t border-dashed pt-1">
              <span>Subtotal</span>
              <span>
                ₹{((charges?.baseAmount || 0) + (charges?.otherAmount || 0) - (payments?.discountAmount || 0)).toFixed(2)}
              </span>
            </div>

            <div className="flex justify-between">
              <span>GST {charges?.gstPercentage}%</span>
              <span>₹{charges?.gstAmount?.toFixed(2)}</span>
            </div>

            <div className="flex justify-between font-bold border-t-2 pt-1">
              <span>Total</span>
              <span>₹{charges?.totalAmount?.toFixed(2)}</span>
            </div>
          </div>

          {/* Payment */}
          <div className="border-t border-dashed pt-1 space-y-0.5 break-inside-avoid">
            <div className="flex justify-between"><span>Paid</span><span>₹{payments?.settledAmount || 0}</span></div>
            <div className="flex justify-between"><span>Advance</span><span>₹{payments?.advancePaid || 0}</span></div>
            <div className="flex justify-between font-semibold">
              <span>Balance</span>
              <span>{balance > 0 ? `₹${balance.toFixed(2)}` : '₹0.00'}</span>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center border-t-2 border-dashed pt-2 break-inside-avoid">
            <p className="font-bold">THANK YOU</p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default BillPrint;